import { css, html, LitElement } from "lit";

import { controlStyles } from "../styles/tokens";

/**
 * <modulina-search-bar> — the explicit-action search row (spec-web-ui §2:
 * Enter/button triggers, NO fetch-per-keystroke). Emits `search`
 * (detail: { q }) only on Enter or the button. The tenant-wide toggle
 * (`allowScopeToggle` + `searchAll`) mirrors the "ieškoti visur" view:
 * visible only when a project is selected — with no project the search is
 * tenant-wide anyway and the checkbox would be noise.
 */
export class ModulinaSearchBar extends LitElement {
  static override properties = {
    q: {},
    busy: { type: Boolean },
    allowScopeToggle: { type: Boolean, attribute: false },
    searchAll: { type: Boolean },
    placeholder: {},
  };

  declare q: string;
  declare busy: boolean;
  declare allowScopeToggle: boolean;
  declare searchAll: boolean;
  declare placeholder: string;

  constructor() {
    super();
    this.q = "";
    this.busy = false;
    this.allowScopeToggle = false;
    this.searchAll = false;
    this.placeholder = "Paieška pagal pavadinimą…";
  }

  static override styles = [
    controlStyles,
    css`
      :host {
        display: flex;
        gap: var(--m-space-2);
        align-items: center;
      }
      input[type="search"] {
        flex: 1;
      }
      .toggle {
        display: flex;
        align-items: center;
        gap: var(--m-space-1);
        font-size: var(--m-fs-small);
        color: var(--m-color-text-muted);
        white-space: nowrap;
      }
      .toggle input {
        width: auto;
      }
    `,
  ];

  override render() {
    return html`
      <input
        type="search"
        .value=${this.q}
        placeholder=${this.placeholder}
        aria-label="Paieška pagal pavadinimą"
        @input=${(e: Event) => (this.q = (e.target as HTMLInputElement).value)}
        @keydown=${this.onKeydown}
      />
      ${this.allowScopeToggle
        ? html`
            <label class="toggle">
              <input
                type="checkbox"
                .checked=${this.searchAll}
                @change=${this.onToggle}
              />
              Ieškoti visur
            </label>
          `
        : null}
      <button class="primary" ?disabled=${this.busy} @click=${this.emitSearch}>
        Ieškoti
      </button>
    `;
  }

  private onKeydown(e: KeyboardEvent) {
    if (e.key === "Enter") this.emitSearch();
  }

  private onToggle(e: Event) {
    this.searchAll = (e.target as HTMLInputElement).checked;
    this.dispatchEvent(
      new CustomEvent("scope-change", {
        detail: { searchAll: this.searchAll },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private emitSearch() {
    this.dispatchEvent(
      new CustomEvent("search", {
        detail: { q: this.q.trim() },
        bubbles: true,
        composed: true,
      }),
    );
  }
}

customElements.define("modulina-search-bar", ModulinaSearchBar);
